import Checkbox from "../../Checkbox/Checkbox";
import { formatCurrency } from "../../../utils";
import TextInput from "../../TextInput/TextInput";
import { headersType, ToastVariants } from "../../../models";
import { useContext, useEffect, useState } from "react";
import { TableHeader, TableRow } from "../../TableComponents/TableComponents";
import Button from "../../Button/Button";
import { DiscountValueRegex } from "../../../utils/constants";
import { ToastContext } from "../../../contexts/ToastContext/ToastContext";
import { Service, ServiceBilledTo, ServiceOwner } from "../../../models/service.model";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { useDispatch } from "react-redux";
import { searchServiceData } from "../../../redux/slices/ServiceSlice";
import { Link } from "react-router-dom";

export const headers: headersType[] = [
  {
    title: "Service",
    extraClasses: "w-full pl-0 pt-0 pb-0 break-all",
    sortKey: "service",
    isClickable: false,
  },
  {
    title: "Rate",
    extraClasses: "w-60 pl-0 pt-0 pb-0",
    sortKey: "price",
    isClickable: false,
  },
  {
    title: "Discount",
    extraClasses: "w-80 pl-0 pt-0 pb-0",
    sortKey: "discount",
    isClickable: false,
  },
  {
    title: "Annual Plan",
    extraClasses: "w-60 pl-0 pr-0 pt-0 pb-0",
    sortKey: "annualPlan",
    isClickable: false,
  },
];

const Step2 = ({
  officeId,
  isEditServices,
  changeStep,
  services,
  setServices,
}: {
  officeId: string;
  isEditServices?: boolean;
  services: Service[];
  setServices: (services: Service[]) => void;
  changeStep: (val?: boolean) => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const [extraClasses] = useState<string[]>(headers.map(head => head.extraClasses));

  const handleChange = (item: Service, index: number, type: string, value: string | boolean) => {
    const newItem = { ...item };
    if (type === "annualPlanEnabled" && typeof value === "boolean") {
      newItem.annualPlanEnabled = value;
    }
    if (type === "annualPlanDiscountPct" && typeof value === "string") {
      const discount = +parseInt((value as string).replace(DiscountValueRegex, "")).toFixed(2) || 0;
      if (discount <= 100 && discount >= 0) {
        newItem.annualPlanDiscountPct = discount;
      } else {
        return;
      }
    }
    setServices([...services.slice(0, index), { ...newItem }, ...services.slice(index + 1)]);
  };

  const handleNext = () => {
    const valid = services.some(item => item.annualPlanEnabled);
    if (!valid) {
      toast?.openToast("You must enable a service to proceed", 2000, ToastVariants.Error);
      return;
    }
    const valid1 = services.filter(item => item.annualPlanEnabled).every(item => item?.annualPlanDiscountPct && item?.annualPlanDiscountPct > 0);
    if (!valid1) {
      toast?.openToast("Please apply discount to selected annual plan service.", 2000, ToastVariants.Error);
      return;
    }
    changeStep();
  };

  useEffect(() => {
    if (officeId && !services.length)
      dispatch(
        searchServiceData({
          page: 0,
          size: 200,
          sort: "name,asc",
          term: "",
          officeId,
          enabledOnly: true,
          serviceOwner: ServiceOwner.Office,
        })
      ).then((res: any) => {
        if (res?.payload?.data?.content?.length) {
          const result = res?.payload?.data?.content.filter((item: Service) => item.serviceBilledTo === ServiceBilledTo.Patient);
          setServices(result);
        }
      });
  }, []);

  return (
    <>
      <div className="mt-10">
        <h2 className="headline font-bold uppercase">{isEditServices ? "Edit" : "plan"} services</h2>
        {services?.length > 0 ? (
          <div className="w-full relative mt-5">
            <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50">
              {headers.map((head, index) => (
                <TableHeader
                  title={head.title}
                  index={index}
                  key={index}
                  sortIcon={null}
                  handleSort={() => null}
                  extraClassesHeader={head.extraClasses}
                  isClickable={head.isClickable}
                />
              ))}
            </div>
            {services?.map((rowData, index) => {
              const price = rowData.price / 100;
              const finalPrice = formatCurrency(price.toString(), "blur");
              const sortedData = {
                service: (
                  <div className="relative group">
                    <span className="line-clamp-1">{rowData.name}</span>
                    <div className="absolute left-0 bottom-full mb-1 hidden group-hover:block bg-gray-800 text-white text-sm p-2 rounded-md z-10 w-max max-w-xs">
                      {rowData.name}
                    </div>
                  </div>
                ),
                price: (
                  <div className={`${extraClasses}`}>
                    <span className="whitespace-normal">{formatCurrency(finalPrice.toString(), "blur")}</span>
                  </div>
                ),
                discount: (
                  <div className="flex gap-1 justify-between items-center h-9">
                    <TextInput
                      isDarkBg
                      label=""
                      value={rowData?.annualPlanDiscountPct && rowData?.annualPlanDiscountPct > 0 ? rowData?.annualPlanDiscountPct?.toString() : ""}
                      placeholder="Discount"
                      onChangeFunc={e => handleChange(rowData, index, "annualPlanDiscountPct", e.target.value)}
                      extraInputClass="!py-1 text-xs"
                    />
                    <span>%</span>
                  </div>
                ),
                annualPlan: (
                  <div key={index} className={`${extraClasses}`}>
                    <Checkbox
                      label=""
                      checked={rowData.annualPlanEnabled}
                      onChange={e => handleChange(rowData, index, "annualPlanEnabled", e.target.checked)}
                    />
                  </div>
                ),
              };
              return (
                <TableRow
                  data={Object.values(sortedData)}
                  key={index}
                  id={""}
                  tooltipVisible={false}
                  extraClassesRow={extraClasses}
                  handleOpenTooltip={() => null}
                  handleCloseTooltip={() => null}
                  tooltipOptions={[]}
                  handleTooltipClick={undefined}
                  onRowClick={() => null}
                  allowGroupHover={false}
                />
              );
            })}
          </div>
        ) : (
          <div className="mt-10">
            <p>
              You have no existing billed to patient services. Please create services{" "}
              <Link to={`/offices/${officeId}/services`} className="text-docsigna-orange">
                here
              </Link>{" "}
              in order to proceed.
            </p>
          </div>
        )}
      </div>
      <div className="fixed bottom-0 right-0 w-11/12 max-w-120 z-30 bg-docsigna-pink-light border-t">
        <div className="px-8 py-5 flex justify-end">
          <Button
            onClick={() => changeStep(true)}
            className="pointer px-5 mr-3"
            text={isEditServices ? "Cancel" : "Previous"}
            width="fit"
            variant="Secondary"
          />
          <div id="save-button">
            <Button onClick={handleNext} className="pointer px-5" text={isEditServices ? "Save" : "Next"} width="fit" variant={"Primary"} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Step2;
